import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Card, CardBody, Icon } from '@repo/ui';

import { showToast } from '@repo/ui';
import { PageLayout } from '@xtnd/web/shared/layout';
import type { ArchivalConfiguration } from '@xtnd/web/features/archival/types/archivalType';
import {
  CreateArchivalConfigModal,
  ExternalArchivalConfigCard,
} from '@xtnd/web/features/archival/components';
import { ArchivalTestModal } from '@xtnd/web/features/archival/components/ArchivalTestModal';
import {
  useArchivalConfigurations,
  useSupportedServices,
  useDeleteConfiguration,
  archivalKeys,
} from '@xtnd/web/features/archival/queries/useArchivalQuery';

/**
 * External Archival Configuration Page
 *
 * Main interface for managing external archival services including:
 * - Service configuration (Azure Blob, AWS S3, OneDrive)
 * - Health monitoring and status dashboard
 * - Configuration testing and validation
 * - Real-time health check results
 */
export const ExternalArchivalPage: React.FC = () => {
  const navigate = useNavigate();
  // Removed activeTab state since we only have configurations now
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isTestModalOpen, setIsTestModalOpen] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState<ArchivalConfiguration | null>(null);

  const queryClient = useQueryClient();

  // Fetch archival configurations
  const {
    data: configurations = [],
    isLoading: isLoadingConfigs,
    error: configsError,
  } = useArchivalConfigurations();

  // Fetch supported services
  const { data: supportedServices = [] } = useSupportedServices();

  // Delete configuration mutation
  const deleteConfigMutation = useDeleteConfiguration();

  const handleCreateConfig = () => {
    setSelectedConfig(null);
    setIsCreateModalOpen(true);
  };

  const handleEditConfig = (config: ArchivalConfiguration) => {
    setSelectedConfig(config);
    setIsCreateModalOpen(true);
  };

  const handleTestConfig = (config: ArchivalConfiguration) => {
    setSelectedConfig(config);
    setIsTestModalOpen(true);
  };

  const handleDeleteConfig = (config: ArchivalConfiguration) => {
    if (confirm(`Are you sure you want to delete the ${config.serviceType} configuration?`)) {
      deleteConfigMutation.mutate({
        tenantId: config.tenantId,
        serviceId: config.serviceId,
      });
    }
  };

  // Removed handleManualHealthCheck function

  const refreshConfigurations = () => {
    queryClient.invalidateQueries({ queryKey: archivalKeys.configurations });
    queryClient.invalidateQueries({ queryKey: archivalKeys.configurationStatus });
    showToast.success('Data refreshed');
  };

  if (configsError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardBody className="text-center py-8">
            <Icon name="HiExclamation" size="2xl" className="text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-xtnd-dark mb-2">
              Failed to Load Archival Data
            </h3>
            <p className="text-gray-600 mb-4">
              {configsError?.message || 'Unknown error occurred'}
            </p>
            <Button
              onClick={refreshConfigurations}
              variant="primary"
              leftIcon={<Icon name="HiRefresh" size="sm" />}
            >
              Retry
            </Button>
          </CardBody>
        </Card>
      </div>
    );
  }

  return (
    <PageLayout
      title="External Archival Services"
      description="Configure and monitor external storage services for invoice archival"
      headerContent={
        <div className="flex items-center gap-3">
          <Button
            onClick={refreshConfigurations}
            variant="outline"
            size="sm"
            leftIcon={<Icon name="HiRefresh" size="sm" />}
            disabled={isLoadingConfigs}
          >
            Refresh
          </Button>
          <Button
            onClick={handleCreateConfig}
            variant="primary"
            leftIcon={<Icon name="HiPlus" size="sm" />}
          >
            Add Service
          </Button>
        </div>
      }
    >
      {/* Main Content */}
      <div className="max-w-7xl 2xl:max-w-none 2xl:mx-8 mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div>
          {isLoadingConfigs ? (
            <div className="flex items-center justify-center py-12">
              <Icon name="LoadingSpinner" size="lg" />
              <span className="ml-3 text-gray-600 dark:text-gray-300">
                Loading configurations...
              </span>
            </div>
          ) : configurations.length === 0 ? (
            <Card className="text-center py-12">
              <CardBody>
                <Icon
                  name="HiArchive"
                  size="md"
                  className="text-gray-300 dark:text-gray-600 mx-auto mb-4"
                />
                <h3 className="text-lg font-semibold text-xtnd-dark dark:text-xtnd-white mb-2">
                  No Archival Services Configured
                </h3>
                <p className="text-gray-600 dark:text-gray-300 mb-6 max-w-md mx-auto">
                  Get started by adding your first external storage service. We support Azure Blob
                  Storage, AWS S3, and OneDrive.
                </p>
                <Button
                  onClick={handleCreateConfig}
                  variant="primary"
                  leftIcon={<Icon name="HiPlus" size="sm" />}
                >
                  Add Your First Service
                </Button>
              </CardBody>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-6">
              {configurations.map(config => {
                // healthData.services is an object, not an array, so we can't use .find()
                // For now, we'll use undefined until health checking is properly implemented
                const healthInfo = undefined;

                return (
                  <ExternalArchivalConfigCard
                    key={`${config.tenantId}-${config.serviceId}`}
                    config={config}
                    healthInfo={healthInfo}
                    onEdit={() => handleEditConfig(config)}
                    onTest={() => handleTestConfig(config)}
                    onDelete={() => handleDeleteConfig(config)}
                    onHealthCheck={() => {}} // Removed health check
                    isHealthCheckLoading={false}
                  />
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <CreateArchivalConfigModal
        isOpen={isCreateModalOpen}
        onClose={() => {
          setIsCreateModalOpen(false);
          setSelectedConfig(null);
        }}
        config={selectedConfig}
        supportedServices={supportedServices}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: archivalKeys.configurations });
          queryClient.invalidateQueries({ queryKey: archivalKeys.configurationStatus });
          setIsCreateModalOpen(false);
          setSelectedConfig(null);
        }}
      />

      <ArchivalTestModal
        isOpen={isTestModalOpen}
        onClose={() => {
          setIsTestModalOpen(false);
          setSelectedConfig(null);
        }}
        config={selectedConfig}
      />
    </PageLayout>
  );
};
